import i18n from "../i18n";
import React, { useState, useEffect, useMemo } from "react";
import _ from "lodash";
import { useServerApi } from "../hooks/userServerApi";
import { ActionIcon, Divider, Menu, Title, Tooltip } from "@mantine/core";
import {
  createStyles,
  Table,
  Progress,
  Anchor,
  Text,
  Group,
  ScrollArea,
  SimpleGrid,
  Button,
  Chip,
  Badge,
  Paper,
  Card,
} from "@mantine/core";
import { useFormatter } from "../hooks/useFomatter";
import { useCellRender } from "../hooks/useCellRender";
import moment from "moment";
import {
  IconUsers,
  IconUser,
  IconCircleCheck,
  IconHourglass,
  IconNotes,
  IconTableImport,
  IconCirclePlus,
  IconUserCircle,
  IconBuildingBank,
  IconCircleMinus,
  IconActivity,
  IconCircleX,
  IconHeart,
  IconReceipt2,
  IconAlertCircle,
  IconDots,
  IconDotsVertical,
  IconRefresh,
} from "@tabler/icons";

import CardStat from "./cardStat";
import { showNotification } from "@mantine/notifications";
import InfoGridList from "./infoGridList";
import { pickCommissionAffectingSnapshot } from "../helpers/commissionOrderChangeUtils";

const CommissionPaymentSummary = ({ payments, infoData, mt = "xl" }) => {
  const [formatter] = useFormatter();
  return (
    <Card>
      <Paper withBorder p="xs">
        <Group>
          <Text size="xs">Total Payments: {infoData.paymentCount}</Text>
          <Text size="xs">
            Total Commission: {formatter.currency(infoData.totalCommission)}
          </Text>
          <Text size="xs">Waiting: {infoData.waiting}</Text>
          <Text size="xs">Received: {infoData.received}</Text>
        </Group>
      </Paper>
    </Card>
  );
};

const OrderCommissionPaymentTable = ({ payments, mt = "xl", fetch }) => {
  const [formatter] = useFormatter();
  const [cellRender] = useCellRender();
  const [api] = useServerApi();

  const resetPayment = async (payment) => {
    try {
      if (!payment) return;
      const id = payment._id;
      const ans = await window.confirm(
        `Are you sure you want to reset this payment ${payment.code} ?`
      );
      if (!ans) return;
      const data = await api.CommissionPayment.resetPayment(id);
      if (_.isFunction(fetch)) fetch();
      showNotification({
        title: `Reset Payment`,
        message: `Payment ${data.code} reset successfully`,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const waitPayment = async (id) => {
    try {
      const data = await api.CommissionPayment.waitPayment(id);
      if (_.isFunction(fetch)) fetch();
      showNotification({
        title: `Skip Payment`,
        message: `Payment ${data.code} skipped successfully`,
      });
    } catch (error) {
      console.log(error);
    }
  };
  const skipPayment = async (id) => {
    try {
      const data = await api.CommissionPayment.skipPayment(id);
      if (_.isFunction(fetch)) fetch();
      showNotification({
        title: `Skip Payment`,
        message: `Payment ${data.code} skipped successfully`,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const rows = payments
    // ?.sort((a, b) => a.code.localeCompare(b.code))
    ?.map((payment, index) => {
      return (
        <tr key={payment.code}>
          <td>
            <Text size="xs">#{index + 1}</Text>
          </td>
          <td>
            <Text size="xs">{payment.expectedPeriod}</Text>
          </td>
          <td>
            <Text size="xs">{payment.year}</Text>
          </td>

          <td style={{ whiteSpace: "nowrap" }}>
            <Text size="xs">{payment.code}</Text>
          </td>

          <td>
            <Text size="xs">
              {formatter.currency(payment.premium, payment.baseCurrency)}
            </Text>
          </td>

          <td>
            <Text size="xs">
              {formatter.percentage(payment.commissionRate * 100)}
            </Text>
          </td>
          <td>
            <Text size="xs">
              {formatter.currency(
                payment.expectedBaseAmount,
                payment.baseCurrency
              )}
            </Text>
          </td>
          <td>
            <Text size="xs">
              {formatter.currency(payment.receivedPaidAmount)}
            </Text>
          </td>
          <td>
            <Text size="xs">{cellRender.Status(payment.status)}</Text>
          </td>
          <td>
            <Menu transition="pop" withArrow position="bottom-end">
              <Menu.Target>
                <ActionIcon>
                  <IconDotsVertical size={16} stroke={1.5} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {/* {payment.status != "WAITING" && payment.status != "SKIPPED" && (
                <Menu.Item>
                <Text size="xs">No Action for status {payment.status}</Text>
                </Menu.Item>
                )} */}

                {(payment.status == "WAITING" ||
                  payment.status == "VERIFING") && (
                  <Menu.Item
                    onClick={() => skipPayment(payment._id)}
                    color="red"
                    icon={<IconCircleX size={16} stroke={1.5} />}
                  >
                    <Text size="xs">{i18n.t("Skip Payment")}</Text>
                  </Menu.Item>
                )}
                {(payment.status == "PAID" || payment.status == "VERIFING") && (
                  <Menu.Item
                    onClick={() => resetPayment(payment)}
                    color="red"
                    icon={<IconRefresh size={16} stroke={1.5} />}
                  >
                    <Text size="xs">{i18n.t("Reset Payment")}</Text>
                  </Menu.Item>
                )}
                {(payment.status == "SKIPPED" ||
                  payment.status == "VERIFING") && (
                  <Menu.Item
                    onClick={() => waitPayment(payment._id)}
                    icon={<IconCircleCheck size={16} stroke={1.5} />}
                  >
                    <Text size="xs">{i18n.t("Wait Payment")}</Text>
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          </td>
        </tr>
      );
    });

  return (
    <Card>
      <Paper withBorder p="xs" sx={{ overflowX: "auto" }}>
        <Table verticalSpacing="xs" sx={{ whiteSpace: "nowrap" }}>
          <thead>
            <tr>
              <th>
                <Text size="xs">#</Text>
              </th>
              <th>
                <Text size="xs">{i18n.t("Period")}</Text>
              </th>
              <th>
                <Text size="xs">{i18n.t("Year")}</Text>
              </th>

              <th>
                <Text size="xs">{i18n.t("Code")}</Text>
              </th>
              <th>
                <Text size="xs">{i18n.t("Premium")}</Text>
              </th>
              <th>
                <Text size="xs">{i18n.t("Rate")}</Text>
              </th>
              <th>
                <Text size="xs">{i18n.t("Expected")}</Text>
              </th>
              <th>
                <Text size="xs">{i18n.t("Received")}</Text>
              </th>

              <th>
                <Text size="xs">{i18n.t("Status")}</Text>
              </th>
              <th>
                <Text size="xs">{i18n.t("Action")}</Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {_.isEmpty(rows) ? (
              <tr>
                <td colSpan={100}>
                  <Text size="xs">{i18n.t("No Commission Payment")}</Text>
                </td>
              </tr>
            ) : (
              rows
            )}
          </tbody>
        </Table>
      </Paper>
    </Card>
  );
};

const PROTECTED_PAYMENT_STATUSES = ["RECEIVED", "PAID", "PARTIAL", "VERIFING"];

const OrderCommissionPaymentList = ({ form, refreshKey = 0 }) => {
  const { _id: orderId, status, payment, bidConsultants } = form.values;
  const isCertainPayment = _.get(form.values, "isCertainPayment");

  const [payments, setPayments] = useState([]);
  const [api] = useServerApi();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [formatter] = useFormatter();
  const [cellRender] = useCellRender();

  const [infoData, setInfoData] = useState({});

  const commissionWatchKey = useMemo(
    () => JSON.stringify(pickCommissionAffectingSnapshot(form.values)),
    [form.values]
  );

  //Fetch Commission Payment for the order if exist
  const fetch = async () => {
    if (!orderId) return;
    try {
      setFetching(true);
      const data = await api.Order.getCommissionPayment(orderId);
      // console.log("fetch", data);
      let payments = data.commissionPayments ?? [];
      // console.log(
      //   "payments",
      //   payments.map((p) => p.code + " : " + p.expectedPeriod)
      // );
      setPayments(payments);
      setInfoData({
        paymentCount: payments.length ?? 0,
        totalCommission: payments?.reduce(
          (a, c) => a + c.expectedBaseAmount,
          0
        ),
        waiting: payments?.filter((r) => r.status === "WAITING")?.length ?? 0,
        partial: payments?.filter((r) => r.status === "PARTIAL")?.length ?? 0,
        received: payments?.filter((r) => r.status === "RECEIVED")?.length ?? 0,

        fyPremium: payment.yearPremium ?? emptyStr,
        fyRate: payment.yearRate ?? emptyStr,
        fyConsultantCommission: payment.yearConsultantCommission ?? emptyStr,
        fyCommission: payment.yearCommission ?? emptyStr,
        baseCurrency: payment?.currency,
        bidConsultants,
      });

      setFetching(false);
    } catch (error) {
      console.log(error);
    }
  };

  const generateCommissionPayment = async () => {
    try {
      if (!orderId) return;
      const ans = await window.confirm(
        "Re-generate commission payments from the current commission setting? This will delete and recreate all waiting/unconfirmed payments."
      );
      if (!ans) return;

      setLoading(true);
      const data = await api.Order.generateCommissionPayment(orderId);
      await fetch();

      showNotification({
        title: `Re-generated successfully`,
        message: `Total ${data?.commissionPayments?.length ?? 0} commission payments generated successfully`,
      });
      setLoading(false);
    } catch (error) {
      showNotification({
        title: `Re-generate failed`,
        message: api.getErrorMsg?.(error) ?? error?.message ?? String(error),
        color: "red",
      });
      setLoading(false);
    }
  };

  const skipOldWaitingPayment = async () => {
    try {
      const ans = await window.confirm(
        `Are you certain that payments with an expected period before the current period (${moment().format(
          "YYYYMM"
        )}) will be skipped?`
      );
      if (!ans) return;

      const data = await api.CommissionPayment.skipOldWaitingPayment(orderId);
      fetch();
      showNotification({
        title: `Skip Waiting Payment`,
        message: `Skipped ${data.numOfSkipped} waiting payments`,
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetch();
  }, [orderId, refreshKey, commissionWatchKey]);

  const isShow = () => true;
  // () => {
  //   const showOnlyStatus = [
  //     "CAN",
  //     "INF",
  //     "WTH",
  //     "SUR",
  //     "TER",
  //     "SUS",
  //     "LAP",
  //     "TRO",
  //     "MAT",
  //     "EXP",
  //     "OTH",
  //   ];
  //   if (!status || !showOnlyStatus.includes(status)) return false;
  //   return true;
  // };

  const emptyStr = "--";

  const hasProtectedPayments = payments.some((p) =>
    PROTECTED_PAYMENT_STATUSES.includes(p.status)
  );
  const canRegenerate =
    !!orderId && isCertainPayment && !hasProtectedPayments && !loading;
  const regenerateDisabledReason = !isCertainPayment
    ? "Only available for certain payment orders"
    : hasProtectedPayments
      ? "Cannot regenerate while received, paid, or verifying payments exist"
      : null;

  const infoFields = [
    { title: "Total Payment Count", value: infoData.paymentCount },
    {
      title: "Total Commission",
      value: formatter.currency(
        infoData.totalCommission,
        infoData.baseCurrency
      ),
    },

    { title: "Waiting ", value: infoData.waiting },
    { title: "Partial ", value: infoData.partial },
    { title: "Received ", value: infoData.received },
  ];

  return (
    <>
      {!isShow() && (
        <Text size={"xs"}>
          Sorry, there are no commission payments for this order currently.
        </Text>
      )}
      {isShow() && (
        <>
          <CommissionPaymentSummary payments={payments} infoData={infoData} />
          <Group position="apart" mt={"xl"}>
            <Group spacing="xs">
              {/* <Badge radius={0}>
                V{(form.values?.commission?.table?.index ?? 0) + 1}
              </Badge>
              <Badge color={isCertainPayment ? "green" : "red"} radius={0}>
                {isCertainPayment ? "Certain" : "Un Certain"}
              </Badge> */}
            </Group>

            <Group spacing={0}>
              <ActionIcon onClick={() => fetch()}>
                <IconRefresh size={16} stroke={1.5} />
              </ActionIcon>
              <Menu transition="pop" withArrow position="bottom-end">
                <Menu.Target>
                  <ActionIcon>
                    <IconDotsVertical size={16} stroke={1.5} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    onClick={() => {
                      skipOldWaitingPayment();
                    }}
                    icon={<IconCircleX size={16} stroke={1.5} />}
                  >
                    <Text size="xs">{i18n.t("Skip All Expired Payments")}</Text>
                  </Menu.Item>
                  <Tooltip
                    label={regenerateDisabledReason ?? ""}
                    disabled={!regenerateDisabledReason}
                    withArrow
                  >
                    <Menu.Item
                      onClick={() => generateCommissionPayment()}
                      disabled={!canRegenerate}
                      color="red"
                      icon={<IconRefresh size={16} stroke={1.5} />}
                    >
                      <Text size="xs">{i18n.t("Re-Generate Payments")}</Text>
                    </Menu.Item>
                  </Tooltip>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
          <OrderCommissionPaymentTable
            payments={payments}
            mt={"xl"}
            fetch={fetch}
          />
        </>
      )}
    </>
  );
};

export default OrderCommissionPaymentList;
